import React, { useState } from "react"
import { Input, Button, Empty, Card, Typography, Dropdown, Menu, Flex } from "antd"
import { SearchOutlined, PlusOutlined, EllipsisOutlined } from "@ant-design/icons"
import styles from "./CustomTemplateHeader.module.css"
import WithEmptyStateWrapper from "ui/WithEmptyStateWrapper"
import TemplateEmptyImage from "ui/Icon/DSIcon/TemplateEmptyImage"
import { Text } from "ui/Typography"
import { useTranslation } from "react-i18next"
import CustomPagination from "components/common/CustomPagination"
import Spinner from "components/common/Spinner"
import { useTemplates } from "./hooks/useTemplates"
import DataTable from "ui/DataTable"
import { useNavigate } from "react-router-dom"
import { routes } from "router"
import { useDispatch, useSelector } from "react-redux"
import { deleteQuestionnaireTemplate, duplicateQuestionnaireTemplate, getQuestionnaireTemplates } from "store/question"
import DeleteConfirmation from "./modal/DeleteConfirmation"
import { toaster } from "components/common/Toast"
import TripSelector from "components/TripSelector"
import { Search } from "ui/Icon/DSIcon/Search"
import FirstLoading from "modules/loader-watchers/FirstLoading"

export const CustomTemplate = () => {
  const [searchText, setSearchText] = useState("")
  const [deleteModal, setDeleteModal] = useState({ visible: false, template: null })
  const [openDropdownId, setOpenDropdownId] = useState(null)

  // const [selectedTripIds, setSelectedTripIds] = useState([])
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { loading: isLoadingDeleteTemplate } = useSelector((state) => state.question.deleteTemplate)
  const { loading: isLoadingUpdateExperience, templateId: updatingTemplateId } = useSelector((state) => state.question.experienceUpdate)
  // Use custom hook for templates data and pagination
  const { templates, loading, metadata, pagination, handlePageChange, handlePageSizeChange } = useTemplates(searchText)
  const handleAddTemplate = () => {
    navigate(routes.proSettingsTemplateDetails())
  }

  const columns = [
    {
      title: t("settings.template_name"),
      dataIndex: "name",
      key: "name",
      onCell: (record) => ({
        onClick: (e) => {
          e.stopPropagation()
          navigate(routes.proSettingsEditTemplateDetails({ id: record.uuid }))
        },
        style: { cursor: "pointer" }
      }),
      render: (text) => <Typography.Text strong>{text}</Typography.Text>
    },
    {
      title: t("settings.template_description"),
      dataIndex: "description",
      key: "description",
      onCell: (record) => ({
        onClick: (e) => {
          e.stopPropagation()
          navigate(routes.proSettingsEditTemplateDetails({ id: record.uuid }))
        },
        style: { cursor: "pointer", maxWidth: 300 }
      }),
      render: (text) => (
        <Typography.Text ellipsis={{ tooltip: text }} style={{ maxWidth: 300 }}>
          {text}
        </Typography.Text>
      )
    },
    {
      title: t("settings.experiences"),
      dataIndex: "trips",
      key: "trips",
      width: 300,
      render: (trips, record) => {
        if (!record) return null
        const isUpdatingThisRow = isLoadingUpdateExperience && updatingTemplateId === record.uuid
        return (
          <div className={styles.trips}>
            {isUpdatingThisRow ? (
              <Spinner size="small" />
            ) : (
              <TripSelector
                uuid={record.uuid}
                trips={trips}
                allTrips={record.experience_list}
                isDisabled={!!openDropdownId && openDropdownId !== record.uuid}
                isOpen={openDropdownId === record.uuid}
                onOpenChange={(isOpen) => setOpenDropdownId(isOpen ? record.uuid : null)}
                onSelect={(selectedIds) => {
                  console.log(selectedIds)
                }}
                isLoading={isUpdatingThisRow}
              />
            )}
          </div>
        )
      }
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item key="edit" onClick={() => handleEditTemplate(record)}>
                {t("settings.edit_action")}
              </Menu.Item>
              <Menu.Item key="duplicate" onClick={() => handleDuplicateTemplate(record)}>
                {t("settings.duplicate_action")}
              </Menu.Item>
              <Menu.Item key="delete" danger onClick={() => handleDeleteClick(record)}>
                {t("settings.delete_action")}
              </Menu.Item>
            </Menu>
          }
          trigger={["click"]}
          placement="bottomRight"
        >
          <Flex justify="center" align="center">
            <EllipsisOutlined style={{ fontSize: "20px", cursor: "pointer" }} />
          </Flex>
        </Dropdown>
      )
    }
  ]

  const handleRowClick = (record) => {
    console.log(record, "record")
  }

  const handleEditTemplate = (template) => {
    navigate(routes.proSettingsEditTemplateDetails({ id: template.uuid }))
  }

  const handleDuplicateTemplate = async (template) => {
    try {
      await dispatch(duplicateQuestionnaireTemplate(template.uuid))
      toaster.success("Template duplicated successfully")
      dispatch(getQuestionnaireTemplates(1, 10))
    } catch (error) {
      toaster.error(error?.message || "Failed to duplicate template")
    }
  }

  const handleDeleteClick = (template) => {
    setDeleteModal({ visible: true, template })
  }

  const handleDeleteConfirm = async () => {
    if (!deleteModal.template) return

    try {
      const result = await dispatch(deleteQuestionnaireTemplate(deleteModal.template.uuid))
      if (result?.message) {
        toaster.success(result.message)
        // Refresh the templates list by triggering a page change
        handlePageChange(1)
      }
    } catch (error) {
      toaster.error(error.message || "Failed to delete template")
    } finally {
      setDeleteModal({ visible: false, template: null })
    }
  }

  const handleDeleteCancel = () => {
    setDeleteModal({ visible: false, template: null })
  }

  return (
    <div style={{ width: "100%" }}>
      <div className={styles.headerBar}>
        <Input
          className={styles.searchInput}
          placeholder="Search"
          prefix={<SearchOutlined style={{ color: "#bdbdbd" }} />}
          allowClear
          onChange={(e) => setSearchText(e.target.value)}
          value={searchText}
        />
        <Button
          color="primary"
          size="large"
          variant="solid"
          className={styles.addButton}
          icon={<PlusOutlined aria-hidden="true" />}
          onClick={handleAddTemplate}
        >
          {t("settings.add_template")}
        </Button>
      </div>

      <FirstLoading name="question.questionnaireTemplates">
        <WithEmptyStateWrapper
          fallback={
            loading ? (
              <Spinner />
            ) : (
              <Card className={styles.contentWrapper}>
                <Empty
                  image={searchText ? <Search /> : <TemplateEmptyImage />}
                  description={
                    <>
                      <Text strong size="lg">
                        {searchText ? t("clients.no_exact_match") : t("settings.not_created_any_template")}
                      </Text>
                      <p style={{ color: "#888" }}>{!searchText && t("settings.your_template_displayed_here")}</p>
                      {!searchText && (
                        <Button color="primary" variant="solid" onClick={handleAddTemplate}>
                          Add Template
                        </Button>
                      )}
                    </>
                  }
                />
              </Card>
            )
          }
          name="question.questionnaireTemplates"
          hasItems={templates.length > 0}
          tag="div"
          className="vstack gap-20"
        >
          {templates.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <DataTable
                columns={columns}
                dataSource={templates.map((template) => ({
                  ...template,
                  key: template.id
                }))}
                pagination={false}
                onRowClick={handleRowClick}
                rowClickKey="uuid"
              />
              <div style={{ marginTop: 24, textAlign: "right" }}>
                <CustomPagination
                  metadata={{
                    ...metadata,
                    page: metadata.page || 1,
                    total_pages: metadata.total_pages || 1,
                    next_page: metadata.next_page || null,
                    prev_page: metadata.prev_page || null
                  }}
                  pageSize={pagination.pageSize}
                  total={metadata.total_results || 0}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                />
              </div>
            </div>
          )}
        </WithEmptyStateWrapper>

        <DeleteConfirmation
          visible={deleteModal.visible}
          onCancel={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          loading={isLoadingDeleteTemplate}
          title={deleteModal.template?.name ? t("settings.delete_template_title") : t("settings.delete_template_default_title")}
          content={
            deleteModal.template?.name ? (
              <>
                <div>
                  {t("settings.delete_confirmation_template")}
                  <strong>{deleteModal.template?.name}?</strong>
                </div>
                <div>{t("settings.delete_confirmation_desc")}</div>
              </>
            ) : null
          }
        />
      </FirstLoading>
    </div>
  )
}